import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Plus } from 'lucide-react'
import { SuggestionForm } from '@brent/core-ui/components'
import type { SuggestionFormData } from '@brent/shared/validation'
import { useCreateUserSuggestion } from '@/hooks/useUserSuggestions'
import { useToast } from '@/hooks/use-toast'
import { searchComposers, getComposerWorks } from '@/services/api/wikidata-lambda'

interface CreateUserSuggestionDialogProps {
  username: string
  memberName: string
}

export const CreateUserSuggestionDialog = ({
  username,
  memberName,
}: CreateUserSuggestionDialogProps) => {
  const [open, setOpen] = useState(false)
  const { toast } = useToast()
  const createMutation = useCreateUserSuggestion(username)

  const handleSubmit = async (data: SuggestionFormData) => {
    try {
      await createMutation.mutateAsync(data)
      toast({
        title: 'Suggestion created',
        description: `Created suggestion for ${memberName}`,
      })
      setOpen(false)
    } catch (error) {
      toast({
        title: 'Failed to create suggestion',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Suggestion
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Suggestion for {memberName}</DialogTitle>
          <DialogDescription>
            Create a new piece suggestion on behalf of this member.
          </DialogDescription>
        </DialogHeader>
        <SuggestionForm
          onSubmit={handleSubmit}
          onCancel={() => setOpen(false)}
          isLoading={createMutation.isPending}
          submitLabel="Create Suggestion"
          commentPlaceholder="Why should the orchestra play this piece?"
          searchComposers={searchComposers}
          searchWorks={getComposerWorks}
        />
      </DialogContent>
    </Dialog>
  )
}
